// Persistent storage utility for PWA
// Uses IndexedDB with localStorage fallback for better iOS Safari support
// Includes obfuscation for sensitive auth tokens

const DB_NAME = 'CoolRunningDB';
const DB_VERSION = 1;
const STORE_NAME = 'auth';
const isDev = process.env.NODE_ENV === 'development';

// Sensitive keys that should be obfuscated
const SENSITIVE_KEYS = ['stay_kool_running_token', 'stay_kool_running_user'];
const OBFUSCATION_KEY = 'SK_RUN_2024';

let db = null;

/**
 * Obfuscate sensitive data using XOR encoding
 * Note: This provides protection against casual inspection but is not cryptographically secure.
 * For maximum security, httpOnly cookies should be used server-side.
 */
const obfuscate = (str) => {
  if (!str) return '';
  try {
    const encoded = btoa(
      str
        .split('')
        .map((char, i) => 
          String.fromCharCode(char.charCodeAt(0) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length))
        )
        .join('')
    );
    return encoded;
  } catch (e) {
    if (isDev) console.warn('Obfuscation error:', e);
    return str;
  }
};

/**
 * Deobfuscate the encoded string
 */
const deobfuscate = (str) => {
  if (!str) return '';
  try {
    const decoded = atob(str);
    return decoded
      .split('')
      .map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length))
      )
      .join('');
  } catch (e) {
    // If decoding fails, the value might be in old plaintext format - return as-is
    if (isDev) console.warn('Deobfuscation error (might be plaintext):', e);
    return str;
  }
};

/**
 * Check if a key is sensitive and should be obfuscated
 */
const isSensitiveKey = (key) => SENSITIVE_KEYS.includes(key);

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      if (isDev) console.warn('IndexedDB not available, falling back to localStorage');
      resolve(null);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
};

// Set item in storage
export const setStorageItem = async (key, value) => {
  // Determine if this is a sensitive key that needs obfuscation
  const shouldObfuscate = isSensitiveKey(key);
  const storageValue = shouldObfuscate 
    ? obfuscate(typeof value === 'string' ? value : JSON.stringify(value))
    : (typeof value === 'string' ? value : JSON.stringify(value));
  
  // Always set in localStorage as backup
  try {
    localStorage.setItem(key, storageValue);
  } catch (e) {
    if (isDev) console.warn('localStorage set failed:', e);
  }

  // Also set in IndexedDB for better persistence
  try {
    const database = await initDB();
    if (database) {
      return new Promise((resolve, reject) => {
        const transaction = database.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put({ key, value: storageValue, timestamp: Date.now() });
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    }
  } catch (e) {
    if (isDev) console.warn('IndexedDB set failed:', e);
  }
  return true;
};

// Get item from storage
export const getStorageItem = async (key) => {
  const shouldDeobfuscate = isSensitiveKey(key);
  
  // Try IndexedDB first
  try {
    const database = await initDB();
    if (database) {
      return new Promise((resolve) => {
        const transaction = database.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);
        request.onsuccess = () => {
          if (request.result) {
            const rawValue = request.result.value;
            resolve(shouldDeobfuscate ? deobfuscate(rawValue) : rawValue);
          } else {
            // Fallback to localStorage
            const localValue = localStorage.getItem(key);
            resolve(shouldDeobfuscate && localValue ? deobfuscate(localValue) : localValue);
          }
        };
        request.onerror = () => {
          const localValue = localStorage.getItem(key);
          resolve(shouldDeobfuscate && localValue ? deobfuscate(localValue) : localValue);
        };
      });
    }
  } catch (e) {
    if (isDev) console.warn('IndexedDB get failed:', e);
  }

  // Fallback to localStorage
  const localValue = localStorage.getItem(key);
  return shouldDeobfuscate && localValue ? deobfuscate(localValue) : localValue;
};

// Remove item from storage
export const removeStorageItem = async (key) => {
  // Remove from localStorage
  try {
    localStorage.removeItem(key);
  } catch (e) {
    if (isDev) console.warn('localStorage remove failed:', e);
  }

  // Remove from IndexedDB
  try {
    const database = await initDB();
    if (database) {
      return new Promise((resolve) => {
        const transaction = database.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
      });
    }
  } catch (e) {
    if (isDev) console.warn('IndexedDB remove failed:', e);
  }
  return true;
};

// Check if storage is available
export const isStorageAvailable = () => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};
